import _ from "lodash";
import React from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "src/store";

import { selectDetailsRollout } from "@dashboard/devices/store";
import { skipToken } from "@reduxjs/toolkit/query";
import { ExactVersionHeader } from "@shared/table/headers/ExactVersionHeader";
import { MajorVersionHeader } from "@shared/table/headers/MajorVersionHeader";
import { StatusHeader } from "@shared/table/headers/StatusHeader";
import { HoverRow } from "@shared/table/HoverRow";
import { MinimalTable, MinimalTableContainer, MinimalTableHeader } from "@shared/table/MinimalTableComponents";
import { TableBody } from "@shared/table/TableBody";
import { CustomTableHeader, TableHeaderRows } from "@shared/table/TableHeader";
import CustomTablePagination from "@shared/table/TablePagination";
import { TableRowCells } from "@shared/table/TableRowCells";
import { getErrorMessage } from "@shared/toasts/useQueryErrorToast";
import { getCoreRowModel, useReactTable } from "@tanstack/react-table";

import { useGetRolloutsByBuildQuery } from "../../api";
import { RolloutSortCriteria, RolloutSortDirection } from "../../api/rtkClient";
import { Rollout } from "../../types";
import { rolloutStatusLabel } from "../../utils/rolloutStatus";

import { columns } from "./RolloutTable.utils";

type Props = {
    codename: string | null;
    workspaceId: number;
};

type RolloutTableParams = {
    pageIndex: number;
    pageSize: number;
    sortCriteria: RolloutSortCriteria;
    sortDirection: RolloutSortDirection;
    version?: string;
    majorVersions: number[];
    statuses: Rollout["status"][];
};

const defaultParams: RolloutTableParams = {
    pageIndex: 0,
    pageSize: 12,
    sortCriteria: "created",
    sortDirection: "Descending",
    majorVersions: [],
    statuses: [],
};

export const RolloutTable = (props: Props) => {
    const [params, setParams] = React.useState<RolloutTableParams>(defaultParams);

    const {
        data: rolloutData,
        isFetching,
        isError,
        error,
        refetch,
    } = useGetRolloutsByBuildQuery(
        props.codename ? { codename: props.codename, workspaceId: props.workspaceId, ...params } : skipToken,
    );
    const data = React.useMemo(() => rolloutData?.list ?? [], [rolloutData]);

    const { t } = useTranslation(["pages"]);

    const details = useSelector(selectDetailsRollout);

    const onSort = (id: string) => {
        const sortCriteria = id as RolloutSortCriteria;
        setParams((prev) => ({
            ...prev,
            sortCriteria,
            sortDirection: prev.sortCriteria === sortCriteria && prev.sortDirection === "Descending" ? "Ascending" : "Descending",
            pageIndex: 0,
        }));
    };

    const onResetFilter = () => {
        setParams((prev) => ({ ...prev, version: undefined, majorVersions: [], statuses: [], pageIndex: 0 }));
    };

    const onVersionChange = (version: string | undefined) => {
        setParams((prev) => ({ ...prev, version, pageIndex: 0 }));
    };

    const onStatusChange = (statuses: Rollout["status"][]) => {
        setParams((prev) => ({ ...prev, statuses, pageIndex: 0 }));
    };

    const onMajorVersionChange = (majorVersions: number[]) => {
        setParams((prev) => ({ ...prev, majorVersions, pageIndex: 0 }));
    };

    const rolloutTable = useReactTable<Rollout>({
        data,
        columns,
        getCoreRowModel: getCoreRowModel(),
    });

    React.useEffect(() => {
        setParams(defaultParams);
    }, [props.codename, props.workspaceId]);

    return (
        <React.Fragment>
            <MinimalTableContainer>
                <MinimalTable>
                    <MinimalTableHeader>
                        <TableHeaderRows
                            headerGroups={rolloutTable.getHeaderGroups()}
                            renderHeader={(header) => (
                                <CustomTableHeader
                                    sortCriteria={params.sortCriteria}
                                    sortDirection={params.sortDirection === "Ascending" ? "asc" : "desc"}
                                    onSort={onSort}
                                    {...header}
                                >
                                    {header.column.id === "version" && (
                                        <React.Fragment>
                                            <MajorVersionHeader
                                                codename={props.codename}
                                                value={params.majorVersions}
                                                onChange={onMajorVersionChange}
                                            />
                                            <ExactVersionHeader value={params.version} onChange={onVersionChange} />
                                        </React.Fragment>
                                    )}
                                    {header.column.id === "status" && (
                                        <StatusHeader
                                            labels={_.mapValues(rolloutStatusLabel, (label) => label())}
                                            value={params.statuses}
                                            onChange={onStatusChange}
                                        />
                                    )}
                                </CustomTableHeader>
                            )}
                        />
                    </MinimalTableHeader>

                    <TableBody
                        noDataMessage={t("pages:rolloutsPage.noRollouts")}
                        table={rolloutTable}
                        loading={isFetching}
                        filter={{
                            isActive: !!params.version || params.majorVersions.length > 0 || params.statuses.length > 0,
                            onResetFilter,
                        }}
                        error={{ isError, onError: () => void refetch(), message: getErrorMessage(error) }}
                    >
                        {rolloutTable.getRowModel().rows.map((row) => (
                            <HoverRow key={row.id} {...row} selected={details?.selectedId === row.original.id}>
                                <TableRowCells row={row} />
                            </HoverRow>
                        ))}
                    </TableBody>
                </MinimalTable>
            </MinimalTableContainer>
            <CustomTablePagination
                count={rolloutData?.count ?? 0}
                rowsPerPage={params.pageSize}
                page={params.pageIndex}
                onPageChange={(_event, pageIndex) => setParams((prev) => ({ ...prev, pageIndex }))}
                onRowsPerPageChange={(e) => setParams((prev) => ({ ...prev, pageSize: Number(e.target.value), pageIndex: 0 }))}
            />
        </React.Fragment>
    );
};
